import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DemoLandingCard from "./DemoLandingCard";
import { demoLandingCopy, getDemoLandingPreview } from "../../lib/demoExperience";
import { useDemoChooser } from "../../lib/demoChooserContext";
import {
  CLASS_LANDING_FIXTURE,
  SCHOOL_LANDING_FIXTURE,
} from "../../lib/demoLandingFixtures";
import { DEV_AUTH_ENABLED } from "../../lib/devAuth";

function formatWhen(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function MarketingDemoCard({ fixture }) {
  const nav = useNavigate();
  const { openClassDemoChooser } = useDemoChooser();

  function handleCta() {
    if (fixture.openChooser === "class") {
      openClassDemoChooser();
      return;
    }
    nav(fixture.to);
  }

  return (
    <DemoLandingCard
      eyebrow={fixture.eyebrow}
      title={fixture.title}
      subtitle={fixture.subtitle}
      stepsLabel={fixture.stepsLabel}
      location={fixture.location}
      chips={fixture.chips}
      cta={fixture.cta}
      ctaColor={fixture.ctaColor}
      onCta={handleCta}
      testId={fixture.testId}
      ctaTestId={fixture.ctaTestId}
    />
  );
}

function SocialDemoCard({ event }) {
  const { openDemoChooser } = useDemoChooser();
  const goingPreview = useMemo(() => getDemoLandingPreview(3), []);
  if (!event) return null;
  const copy = demoLandingCopy(event);
  const counts = event.counts || { total: 0 };

  return (
    <DemoLandingCard
      featured
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle}
      stepsLabel={copy.stepsLabel}
      location={event.location}
      whenLabel={formatWhen(event.starts_at)}
      goingPreview={goingPreview}
      goingTotal={counts.total}
      pricing={event.pricing}
      rsvpCount={counts.total}
      cta={copy.cta}
      ctaColor="accent"
      onCta={openDemoChooser}
      testId="demo-landing-social"
      ctaTestId="demo-landing-cta"
    />
  );
}

export default function DemoLandingSection({ event }) {
  return (
    <section className="mb-12 lg:mb-16" data-testid="demo-landing-section">
      <div className="mb-5 lg:mb-6">
        <h2 className="font-display font-semibold text-[clamp(1.25rem,3vw,1.5rem)] text-ink text-balance">
          Test the demo
        </h2>
        <p className="mt-1.5 max-w-lg text-[14px] font-normal text-ink/55 text-pretty">
          No account required. Select a social, class, or school to see live RSVPs in action.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:gap-2.5">
        <SocialDemoCard event={event} />
        <MarketingDemoCard fixture={CLASS_LANDING_FIXTURE} />
        <MarketingDemoCard fixture={SCHOOL_LANDING_FIXTURE} />
      </div>
      {DEV_AUTH_ENABLED ? (
        <p className="mt-4 font-ui text-[13px] text-ink/50 text-pretty">
          Class and school cards open offline demos (local fixtures). For the live seeded school,
          use the Dev bar as Marta after <code className="text-ink/70">npm run seed:test-world</code>.
        </p>
      ) : null}
    </section>
  );
}
